import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import { categories, MAIN_SITE_URL } from '@/mocks/categories';

export default function Header() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > 24);
    }
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  function goToSection(id: string) {
    setMobileOpen(false);
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
      }, 120);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }
  }

  const navLinks = [
    { label: t('nav.home'), to: '/' },
    { label: t('nav.buyingGuide'), to: '/article/how-to-use-allchinabuy-spreadsheet' },
    { label: t('nav.guide'), to: '/article/allchinabuy-spreadsheet-guide' },
  ];

  return (
    <header className="sticky top-0 z-40 border-b border-background-200 bg-background-50/95 backdrop-blur-sm">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 md:px-6">
        <Link to="/" className="flex items-center gap-2 cursor-pointer">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-table-line text-lg"></i>
          </span>
          <span className="flex flex-col leading-tight">
            <span className="font-heading text-base font-semibold text-foreground-950 whitespace-nowrap">
              {t('brand.name')}
            </span>
            <span className="hidden text-xs text-foreground-500 sm:block">{t('brand.tagline')}</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 md:flex">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`rounded-full px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                location.pathname === link.to
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-foreground-700 hover:bg-background-100'
              }`}
            >
              {link.label}
            </Link>
          ))}
          <button
            type="button"
            onClick={() => goToSection('categories')}
            className="rounded-full px-4 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100 cursor-pointer whitespace-nowrap"
          >
            {t('nav.categories')}
          </button>
        </nav>

        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          <a
            href={MAIN_SITE_URL}
            target="_blank"
            rel="noopener nofollow"
            className="hidden items-center gap-2 rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 cursor-pointer whitespace-nowrap sm:inline-flex"
          >
            {t('buttons.allchinabuySpreadsheet')}
            <i className="ri-arrow-right-up-line"></i>
          </a>
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className="flex h-9 w-9 items-center justify-center rounded-md border border-background-200 text-foreground-700 cursor-pointer md:hidden"
            aria-label="Menu"
          >
            <i className={mobileOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="max-h-[calc(100vh-64px)] overflow-y-auto border-t border-background-200 bg-background-50 px-4 py-3 md:hidden">
          <div className="flex flex-col gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="rounded-md px-3 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 cursor-pointer"
              >
                {link.label}
              </Link>
            ))}

            <button
              type="button"
              onClick={() => goToSection('categories')}
              className="rounded-md px-3 py-2.5 text-left text-sm font-semibold text-foreground-900 hover:bg-background-100 cursor-pointer"
            >
              {t('nav.categories')}
            </button>

            <div className="mt-1 grid grid-cols-2 gap-1 border-t border-background-200 pt-2">
              {categories.map((cat) => (
                <a
                  key={cat.key}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground-700 hover:bg-background-100 cursor-pointer"
                >
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-background-100 text-foreground-600">
                    <i className={cat.icon}></i>
                  </span>
                  <span className="truncate">{t('categories.' + cat.key)}</span>
                </a>
              ))}
            </div>

            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="mt-2 inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 cursor-pointer whitespace-nowrap"
            >
              {t('buttons.allchinabuySpreadsheet')}
              <i className="ri-arrow-right-up-line"></i>
            </a>
          </div>
        </div>
      )}
    </header>
  );
}